import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { PageShell, PageHero } from "@/components/layout/PageShell";
import { ArrowLeft, Users, Info, CheckCircle2, Crown, Shield } from "lucide-react";

const DistrictDetail = () => {
  const { slug } = useParams();
  const { t, tf, lang } = useLang();
  const [district, setDistrict] = useState(null);

  useEffect(() => { api.district(slug).then(setDistrict).catch(() => setDistrict(false)); }, [slug]);

  if (district === false) return <PageShell><div className="gmoaa-container py-20 text-center text-gmoaa-text-muted">District not found.</div></PageShell>;
  if (!district) return <PageShell><div className="gmoaa-container py-20 text-center text-gmoaa-text-muted">Loading…</div></PageShell>;

  const leaderName = (l) => (l && l.approved ? (lang === "gu" ? l.name_gu : l.name_en) : "—");
  const members = district.members || [];

  return (
    <PageShell>
      <PageHero title={tf(district, "name")} subtitle={district.name_gu} />
      <div className="gmoaa-container py-10">
        <Link to="/districts" className="mb-6 inline-flex items-center gap-1 text-sm font-medium text-gmoaa-green hover:text-gmoaa-saffron-hover">
          <ArrowLeft size={15} /> {t("backToDistricts")}
        </Link>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="flex gap-4 rounded-xl border border-gmoaa-border-subtle bg-white p-5">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-gmoaa-saffron-light text-gmoaa-saffron-hover"><Crown size={22} /></span>
            <div><p className="text-xs uppercase tracking-wide text-gmoaa-text-muted">District President of Union</p><p className="mt-1 font-serif text-lg font-semibold text-gmoaa-text-dark">{leaderName(district.president)}</p></div>
          </div>
          <div className="flex gap-4 rounded-xl border border-gmoaa-border-subtle bg-white p-5">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-gmoaa-green-light text-gmoaa-green"><Shield size={22} /></span>
            <div><p className="text-xs uppercase tracking-wide text-gmoaa-text-muted">{t("coordinator")}</p><p className="mt-1 font-serif text-lg font-semibold text-gmoaa-text-dark">{leaderName(district.coordinator)}</p></div>
          </div>
        </div>

        {/* Public member list */}
        <div className="mt-8">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-gmoaa-green"><Users size={20} /> Medical Officers</h2>
            <span className="text-sm text-gmoaa-text-muted" data-testid="district-member-count">{district.verified} of {district.total} verified</span>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {members.map((m) => (
              <div key={m.id} data-testid={`district-member-${m.id}`} className="flex items-center gap-3 rounded-xl border border-gmoaa-border-subtle bg-white p-3">
                <span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-gmoaa-green-light text-gmoaa-green">
                  {m.photo_url ? <img src={m.photo_url} alt={m.name_en} className="h-full w-full object-cover" /> : (m.name_en?.replace("Dr. ", "")[0] || "?")}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5 font-medium text-gmoaa-text-dark">
                    {lang === "gu" ? m.name_gu : m.name_en}
                    {m.verified && <CheckCircle2 size={14} className="text-gmoaa-green" title="Verified" />}
                  </p>
                  <p className="truncate text-xs text-gmoaa-text-muted">
                    {lang === "gu" ? m.designation_gu : m.designation_en}
                    {m.taluka ? ` · ${m.taluka}` : ""}{m.dispensary ? ` · ${m.dispensary}` : ""}
                  </p>
                </div>
              </div>
            ))}
            {members.length === 0 && <p className="text-sm text-gmoaa-text-muted">No members listed for this district yet.</p>}
          </div>
        </div>

        <div className="mt-6 flex items-start gap-3 rounded-xl border border-dashed border-gmoaa-border-strong bg-gmoaa-surface p-4">
          <Info size={18} className="mt-0.5 shrink-0 text-gmoaa-green" />
          <p className="text-xs text-gmoaa-text-muted">Mobile numbers and emails are never shown in member lists. Green tick = identity verified.</p>
        </div>
      </div>
    </PageShell>
  );
};

export default DistrictDetail;
